<template>
<div>
  <v-app-bar
    app
    :color="primaryColor"
    height="64"
    width="100vw"
    :class="['gift-card-app-bar', { 'no-store-logo': !hasStoreLogo }]"
    :style="{ '--header-text': '#ffffff' }"
  >
    <a v-if="hasStoreLogo" :href="owaUrl" class="logo-link">
      <v-img
        alt="Store Logo"
        class="shrink"
        contain
        :src="getMainImage"
        @error="handleStoreLogoError"
        transition="scale-transition"
        height="36"
        max-width="64"
      />
    </a>

    <div v-if="isMobile" class="mobile-header-actions" :class="{ 'no-store-logo': !hasStoreLogo }">
      <v-btn
        icon
        class="drawer-toggle"
        @click="openDrawer"
        aria-label="Open menu"
      >
        <v-icon color="white" size="24">menu</v-icon>
      </v-btn>
    </div>

    <div v-if="!isMobile" class="nav-row">
      <v-btn v-if="owaLocationLink" text :href="owaLocationLink" class="nav-item">
        {{ $vuetify.lang.t('$vuetify.header.location') }}
      </v-btn>
      <v-btn v-if="owaUrl" text :href="owaUrl" class="nav-item">
        {{ $vuetify.lang.t('$vuetify.header.order') }}
      </v-btn>
      <v-btn v-if="allowReservation" text :href="reservationLink" class="nav-item">
        {{ $vuetify.lang.t('$vuetify.header.reservation') }}
      </v-btn>
      <v-btn v-if="allowLineup" text :href="lineupLink" class="nav-item">
        {{ $vuetify.lang.t('$vuetify.header.lineup') }}
      </v-btn>
      <v-btn
        text
        @click="goToOwnRoute('GiftCard')"
        class="nav-item"
        :class="{ 'nav-item-active': isOwnRouteActive('GiftCard') }"
        :aria-current="isOwnRouteActive('GiftCard') ? 'page' : null"
      >
        {{ $vuetify.lang.t('$vuetify.header.giftcard') }}
      </v-btn>
      <v-btn
        text
        @click="goToOwnRoute('Deposit')"
        class="nav-item"
        :class="{ 'nav-item-active': isOwnRouteActive('Deposit') }"
        :aria-current="isOwnRouteActive('Deposit') ? 'page' : null"
      >
        {{ $vuetify.lang.t('$vuetify.header.deposit') }}
      </v-btn>
    </div>

    <v-spacer></v-spacer>

    <div class="lang-menu-wrap">
      <v-btn
        text
        class="lang-menu-button"
        @click.stop="toggleLanguageMenu"
        aria-label="Choose language"
      >
        <span class="lang-menu-inner">
          <v-icon color="white" size="24">language</v-icon>
          <span v-if="!isMobile" class="desktop-language-label">{{ selectedLanguageLabel }}</span>
        </span>
      </v-btn>

      <div v-if="languageMenu" class="language-menu-panel">
        <button
          v-if="hasZh"
          type="button"
          class="language-option language-option-normal"
          :class="{ selected: $vuetify.lang.current === 'zh' }"
          :style="languageOptionStyle('zh')"
          @click="changeLang('zh')"
        >
          中文
        </button>
        <button
          v-if="hasEn"
          type="button"
          class="language-option language-option-normal"
          :class="{ selected: $vuetify.lang.current === 'en' }"
          :style="languageOptionStyle('en')"
          @click="changeLang('en')"
        >
          English
        </button>
        <button
          v-if="hasFr"
          type="button"
          class="language-option language-option-normal"
          :class="{ selected: $vuetify.lang.current === 'fr' }"
          :style="languageOptionStyle('fr')"
          @click="changeLang('fr')"
        >
          Français
        </button>
      </div>
    </div>
  </v-app-bar>

  <!-- Mobile drawer -- a plain div, NOT v-navigation-drawer, and a SIBLING of
       v-app-bar above, not nested inside it. Two separate bugs stacked here:
       (1) v-navigation-drawer's own internal isActive/computedTransform state
       sets its OWN inline transform reactively (VNavigationDrawer.js
       styles()), fighting any CSS override -- switched to a plain div (same
       as reservation's own .mobile-drawer, header.component.html/css) to
       route around that entirely. (2) v-app-bar ALSO always sets its own
       inline `transform: translateY(...)` (VAppBar.js styles(), present even
       when the computed value is 0) -- any transform on an ancestor creates a
       new containing block for a `position: fixed` descendant, which
       silently reparents fixed positioning to that ancestor's box instead of
       the viewport. That's why the drawer stayed a narrow strip / transparent
       even as a plain div with fixed positioning: it was still nested inside
       v-app-bar. Moving it to a true sibling removes v-app-bar from its
       containing-block chain entirely. -->
  <div
    v-if="drawerOpen"
    class="mobile-drawer"
    :class="{ open: drawerVisible }"
    :style="drawerStyle"
  >
    <v-btn icon class="drawer-close-btn" @click="closeDrawer">
      <v-icon color="white">close</v-icon>
    </v-btn>
    <ul class="drawer-list">
      <li v-if="owaLocationLink">
        <a :href="owaLocationLink" class="drawer-item">{{ $vuetify.lang.t('$vuetify.header.location') }}</a>
      </li>
      <li v-if="owaUrl">
        <a :href="owaUrl" class="drawer-item">{{ $vuetify.lang.t('$vuetify.header.order') }}</a>
      </li>
      <li v-if="allowReservation">
        <a :href="reservationLink" class="drawer-item">{{ $vuetify.lang.t('$vuetify.header.reservation') }}</a>
      </li>
      <li v-if="allowLineup">
        <a :href="lineupLink" class="drawer-item">{{ $vuetify.lang.t('$vuetify.header.lineup') }}</a>
      </li>
      <li>
        <button
          type="button"
          class="drawer-item"
          :class="{ 'drawer-item-active': isOwnRouteActive('GiftCard') }"
          :aria-current="isOwnRouteActive('GiftCard') ? 'page' : null"
          @click="goToOwnRoute('GiftCard'); closeDrawer()"
        >{{ $vuetify.lang.t('$vuetify.header.giftcard') }}</button>
      </li>
      <li>
        <button
          type="button"
          class="drawer-item"
          :class="{ 'drawer-item-active': isOwnRouteActive('Deposit') }"
          :aria-current="isOwnRouteActive('Deposit') ? 'page' : null"
          @click="goToOwnRoute('Deposit'); closeDrawer()"
        >{{ $vuetify.lang.t('$vuetify.header.deposit') }}</button>
      </li>
    </ul>
  </div>
</div>
</template>

<script>
import { get } from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { primaryTextColor } from '@/plugins/util';

export default {
  computed: {
    primaryColor() {
      return this.$store.getters.getManifest?.meta?.primary || '#202124'
    },
    primaryTextColor() { return primaryTextColor(this.$store.getters.getManifest?.meta?.primary) },
    drawerStyle() {
      return {
        backgroundColor: this.primaryColor,
        color: this.primaryTextColor
      }
    },
    owaUrl() {
      // Prioritize the return url for when multiple stores direct to the same storeId
      return this.$parent.returnUrl || this.OWALink
    },


    hasEn() {
      return !!get(this.store, 'languages', []).find(lang => lang.code == 'en')
    },
    hasZh() {
      return !!get(this.store, 'languages', []).find(lang => lang.code == 'zh')
    },
    hasFr() {
      return !!get(this.store, 'languages', []).find(lang => lang.code == 'fr')
    },
    getMainImage() {
      const relativePath = get(this.store, 'images.LOGO[0].url', null) ||
                            get(this.store, 'images.MAIN[0].url', null)

      if (relativePath)
        return `${this.origin}/v1${relativePath}`
      return ''
    },
    hasStoreLogo() {
      return !!this.getMainImage && !this.logoFailed
    },

    // Location: OWA's own "Location" nav item opens its multi-store group picker
    // (HeaderBar.vue's onGroupButtonClick), a page internal to OWA's router --
    // gift card has no equivalent of its own, so this links out to that same
    // group page rather than trying to reproduce it here.
    owaLocationLink() {
      return this.OWALinkGroup
    },
    reservationLink() {
      const storeId = get(this.getStore, 'storeId')
      if (!storeId) return null
      let url = `${this.origin}/reservation/?storeId=${storeId}`
      url += `&returnUrl=${window.location.href}`
      return url
    },
    lineupLink() {
      const storeId = get(this.getStore, 'storeId')
      if (!storeId) return null
      let url = `${this.origin}/lineup/?storeId=${storeId}&force=true&noSms=true`
      url += `&returnUrl=${window.location.href}`
      return url
    },
    // Same feature flags OWA's HeaderBar.vue reads off the same Store object
    // (features.cpReservationSupported/inStoreReservationSupported/
    // settings.webReservationSettings.webReservationEnabled, features.
    // webLineupSupported/inStoreLineupSupported/settings.webLineupSettings.
    // webLineupEnabled and settings.owaSettings.hideLineup) -- kept identical so
    // a store that hides Reservations/Waitlist in OWA hides them here too.
    allowReservation() {
      return get(this.getStore, 'features.cpReservationSupported', false)
        && get(this.getStore, 'features.inStoreReservationSupported', false)
        && get(this.getStore, 'settings.webReservationSettings.webReservationEnabled', false)
    },
    allowLineup() {
      return !get(this.getStore, 'settings.owaSettings.hideLineup', false)
        && get(this.getStore, 'features.webLineupSupported', false)
        && get(this.getStore, 'settings.webLineupSettings.webLineupEnabled', false)
        && get(this.getStore, 'features.inStoreLineupSupported', false)
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    selectedLanguageLabel() {
      if (this.$vuetify.lang.current === 'zh') {
        return '中文'
      }
      if (this.$vuetify.lang.current === 'fr') {
        return 'FRANÇAIS'
      }
      return 'ENGLISH'
    },

    ...mapGetters({
      store: 'getBuild',
      getStore: 'getStore',
      origin: 'origin',
      authToken: 'getAuthToken',
      OWALink: 'OWALink',
      OWALinkGroup: 'OWALinkGroup'
    })
  },

  data: () => ({
    // Two-flag pattern (matches reservation's mobileMenuVisible/mobileMenuOpen,
    // header.component.ts/html): drawerOpen mounts the element (v-if), then
    // drawerVisible is flipped on the next tick to add the .open class --
    // needed for the CSS transform transition to actually animate instead of
    // snapping straight to its open position on mount.
    drawerOpen: false,
    drawerVisible: false,
    languageMenu: false,
    logoFailed: false
  }),

  watch: {
    getMainImage() {
      this.logoFailed = false
    }
  },

  methods: {
    handleStoreLogoError() {
      this.logoFailed = true
    },
    toggleLanguageMenu() {
      this.languageMenu = !this.languageMenu
    },
    closeLanguageMenu() {
      this.languageMenu = false
    },
    openDrawer() {
      this.drawerOpen = true
      this.$nextTick(() => { this.drawerVisible = true })
    },
    closeDrawer() {
      this.drawerVisible = false
      // Keep the element mounted through the close transition (matches
      // reservation's own drawer), then remove it once the slide-out finishes.
      window.setTimeout(() => { this.drawerOpen = false }, 200)
    },
    logout() {
      this.setAuthUser(null)
      this.setAuthToken(null)
    },

    // Deposit/Coupon are routes inside THIS app (see routes.js), unlike
    // Reservations/Waitlist/Location/Order which live in other repos and always
    // need a real navigation -- router.push keeps this a same-app transition
    // instead of a full reload for a page we're already inside.
    // ponytail: exact route match only. Checkout/Confirmation are shared by all
    // three flows with nothing in the route to say which one you came from, so
    // no tab is bold there rather than guessing wrong.
    isOwnRouteActive(name) {
      if (name === 'GiftCard') {
        return this.$route.name === 'GiftCard' || this.$route.name === 'Home'
      }
      return this.$route.name === name
    },
    goToOwnRoute(name) {
      const storeId = get(this.getStore, 'storeId') || this.$route.params.storeId
      if (this.$route.name === name) return
      this.$router.push({ name, params: { storeId } }).catch(() => {})
    },

    changeLang(lang) {
      if (this.$vuetify.lang.current === lang) return; // Don't change if already selected

      this.$vuetify.lang.current = lang;
      window.i18n.locale = lang;
      localStorage.setItem('lang', lang);

      this.fetchStoreDetails();
    },

    matchTranslation(translations, value='value') {
      if (!translations)
        return null

      let lang = this.$vuetify.lang.current

      // Look for the currently set translation
      return (translations.find(translation => translation.lang == lang) || {})[value]
    },
    languageOptionStyle(lang) {
      if (this.$vuetify.lang.current !== lang) {
        return {}
      }

      return {
        backgroundColor: this.primaryColor,
        color: primaryTextColor(this.primaryColor)
      }
    },

    ...mapMutations([ 'setAuthUser', 'setAuthToken' ]),
    ...mapActions([ 'fetchStoreDetails', 'fetchDtStore' ])
  },
}
</script>

<style>
  .gift-card-app-bar {
    box-shadow: none !important;
    overflow: visible !important;
  }

  .gift-card-app-bar .v-toolbar__content {
    min-height: 64px !important;
    padding: 0 18px 0 12px !important;
    overflow: visible !important;
  }

  .gift-card-app-bar.no-store-logo .v-toolbar__content {
    padding-left: 12px !important;
  }

  @media (max-width: 959px) {
    .gift-card-app-bar .v-toolbar__content {
      padding: 0 18px 0 0 !important;
    }

    .gift-card-app-bar.no-store-logo .v-toolbar__content {
      padding-left: 12px !important;
    }
  }

  .logo-link {
    display: inline-flex;
    align-items: center;
    margin-left: 0 !important;
    position: relative;
    z-index: 1;
  }

  .mobile-header-actions {
    display: flex;
    align-items: center;
  }

  .mobile-header-actions.no-store-logo {
    margin-left: 12px;
  }

  .nav-row {
    display: flex;
    align-items: center;
    height: 64px;
  }

  .nav-row .nav-item,
  .nav-item {
    height: 64px !important;
    min-width: 0 !important;
    margin: 0 !important;
    padding: 0 14px !important;
    border-radius: 0 !important;
    font-family: 'Poppins', Helvetica, Arial, sans-serif !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    letter-spacing: normal !important;
    text-transform: uppercase !important;
    color: rgba(255, 255, 255, 0.87) !important;
    opacity: 1;
    background: transparent !important;
    box-shadow: none !important;
    white-space: nowrap;
  }

  .nav-row .nav-item .v-btn__content,
  .nav-item .v-btn__content {
    color: inherit !important;
    font-family: inherit !important;
    font-size: inherit !important;
    font-weight: inherit !important;
    letter-spacing: inherit !important;
    text-transform: inherit !important;
  }

  .nav-row .nav-item:hover,
  .nav-item:hover {
    color: #fff !important;
  }

  .nav-row .nav-item-active,
  .nav-item-active {
    display: inline-flex;
    align-items: center;
    color: #fff !important;
    font-weight: 700 !important;
    opacity: 1;
    white-space: nowrap;
  }

  .drawer-toggle {
    margin: 0 !important;
    min-width: 36px !important;
    width: 36px !important;
    height: 36px !important;
    border-radius: 0 !important;
    color: rgba(255, 255, 255, 0.87) !important;
  }

  .lang-menu-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 64px;
    width: fit-content;
    z-index: 2000;
  }

  .lang-menu-button {
    margin: 0 !important;
    min-width: 0 !important;
    width: auto !important;
    height: 64px !important;
    padding: 0 12px 0 8px !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    color: rgba(255, 255, 255, 0.87) !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
  }

  .lang-menu-inner {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .desktop-language-label {
    font-family: 'Poppins', Helvetica, Arial, sans-serif !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    letter-spacing: normal !important;
    text-transform: uppercase !important;
    color: rgba(255, 255, 255, 0.87) !important;
    white-space: nowrap;
  }

  .drawer-toggle .v-icon,
  .lang-menu-button .v-icon,
  .lang-menu-button .desktop-language-label {
    color: rgba(255, 255, 255, 1) !important;
  }

  .drawer-toggle:hover .v-icon,
  .lang-menu-button:hover .v-icon,
  .lang-menu-button:hover .desktop-language-label {
    color: rgba(255, 255, 255, 1) !important;
  }

  .language-menu-panel {
    position: absolute;
    top: 100%;
    right: 0;
    width: 100%;
    min-width: 100%;
    background: #ffffff;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    border-radius: 0;
    overflow: hidden;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    margin-top: 0;
  }

  @media (max-width: 959px) {
    .language-menu-panel {
      min-width: 120px;
      width: 120px;
    }
  }

  .language-option {
    width: 100%;
    border: none;
    background: #ffffff;
    min-height: 48px !important;
    padding: 14px 16px !important;
    text-align: center;
    font-family: 'Poppins', Helvetica, Arial, sans-serif;
    font-size: 13px;
    line-height: 1.2;
    color: rgba(0, 0, 0, 0.85);
    cursor: pointer;
    outline: none;
    letter-spacing: 0;
  }

  .language-option.selected {
    font-weight: 700;
  }

  .language-option:not(.selected) {
    background: #ffffff;
    color: rgba(0, 0, 0, 0.85);
  }

  .drawer-item-active {
    font-weight: 700 !important;
  }

  /* Full-viewport slide-in panel -- a plain div, byte-matches reservation's
     own .mobile-drawer (header.component.css) exactly: fixed, 100vw/100vh,
     translateX transform toggled by the .open class, no Vuetify component
     between this CSS and what's on screen to fight (see the template
     comment on why v-navigation-drawer was dropped for this). */
  .mobile-drawer {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 2147483647 !important;
    display: flex !important;
    flex-direction: column !important;
    transform: translateX(-100%);
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .mobile-drawer.open {
    transform: translateX(0);
  }
  .drawer-toggle .v-icon,
  .drawer-close-btn .v-icon {
    color: #fff !important;
  }
  .mobile-drawer .drawer-close-btn {
    position: absolute;
    top: 20px;
    left: 16px;
    width: 36px;
    height: 36px !important;
    margin: 0;
  }
  /* Match OWA's drawer: 75px top inset, 36px buttons with 6px vertical
     margins, and an 8px gap between rows. */
  .drawer-list {
    list-style: none;
    margin: 0;
    padding: 75px 0 8px;
  }
  .drawer-list > li {
    padding: 6px 24px;
  }
  .drawer-list > li + li {
    margin-top: 8px;
  }
  .drawer-item {
    display: flex !important;
    align-items: center;
    min-height: 36px;
    width: 100% !important;
    text-align: left !important;
    background: transparent !important;
    border: none !important;
    color: #fff !important;
    font-family: 'Poppins', Helvetica, Arial, sans-serif;
    font-size: 14px !important;
    font-weight: 700 !important;
    line-height: 21px;
    letter-spacing: normal;
    text-transform: uppercase;
    text-decoration: none !important;
    padding: 0 16px !important;
    box-sizing: border-box !important;
    cursor: pointer !important;
  }
  @media (max-width: 480px) {
    .drawer-item {
      padding: 0 4px !important;
    }
  }
</style>
